import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { BreakpointsPx } from '@jsmdg/imgix';
import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { DiscountBadge, DiscountBadgeShape, Image, ResponsiveFrame, Slider } from '@jsmdg/yoshi';
import { PRODUCT_IMG_DIMENSIONS, PRODUCT_IMG_DIMENSIONS_PREVIEW } from '../../../shared/dimensions';
import { MediaKind } from '../../../shared/mediaKind';
import { useProduct } from '../../hooks/useProduct';
import { type ProductDetailContext } from '../../types/types';
import { buildGalleryMediaItems } from '../ImageGallery/galleryMedia';
import { trackOnClickImageSliderOfNewGallery } from '../ImageGallery/imageGallery.tracking';
import { Video } from '../ImageGallery/Video';
import styles from './MediaSlider.module.scss';

const messages = defineMessages({
    previousSlide: {
        defaultMessage: 'Zum vorherigen Bild',
    },
    nextSlide: {
        defaultMessage: 'Zum nächsten Bild',
    },
});

export const MediaSlider = ({
    activeSlideIndex,
    handleIndexChange,
    handleVideoFullscreenChange,
    setIsModalOpen,
    withAddOns,
    withImagePreview,
}: {
    readonly withImagePreview: boolean;
    readonly withAddOns: boolean;
    readonly setIsModalOpen?: (value: boolean) => void;
    readonly activeSlideIndex?: number;
    readonly handleIndexChange?: (index: number, url: string) => void;
    readonly handleVideoFullscreenChange?: (isFullscreen: boolean) => void;
}): JSX.Element => {
    const { discountBadge, mediaContent, title } = useProduct();
    const intl = useIntl();
    const { isBoxProductWithQrCodeLink, isMydays } = useFragmentContext<ProductDetailContext>();
    const showDiscountBadge = !isBoxProductWithQrCodeLink && Boolean(discountBadge);
    const currentDimensions = withImagePreview
        ? PRODUCT_IMG_DIMENSIONS_PREVIEW[BreakpointsPx.xs]
        : PRODUCT_IMG_DIMENSIONS[BreakpointsPx.xs];
    const mediaItems = buildGalleryMediaItems(mediaContent);

    const handleImageClick = (): void => {
        if (!setIsModalOpen) {
            return;
        }

        trackOnClickImageSliderOfNewGallery();
        setIsModalOpen(true);
    };

    return (
        <Slider
            activeSlideIndex={activeSlideIndex}
            onSlideChange={i => {
                handleIndexChange?.(i, mediaItems[i]?.trackingUrl ?? '');
            }}
            classes={{
                swiperWrapper: classNames(
                    withImagePreview && styles.imagePreviewStyling,
                    withImagePreview && 'rounded-2',
                ),
                swiperSlide: styles.swiperSlide,
                controlsContainer: styles.controlsContainer,
                controlsWrapper: styles.controlsWrapper,
            }}
            config={{
                slidesPerView: withImagePreview ? 1.175 : 1,
                slidesPerGroup: 1,
                spaceBetween: withImagePreview ? 12 : 0,
                showPagination: true,
                showNavigation: false,
            }}
            isFreeModeEnabled={false}
            slides={mediaItems.map((mediaItem, index) => ({
                key: mediaItem.key,
                content: (
                    <ResponsiveFrame
                        width={currentDimensions.width}
                        height={currentDimensions.height}
                    >
                        <div className="w-100 h-100">
                            {showDiscountBadge && withAddOns && (
                                <DiscountBadge
                                    percentage={discountBadge?.percentage}
                                    text={discountBadge?.text}
                                    shape={
                                        isMydays
                                            ? DiscountBadgeShape.Pill
                                            : DiscountBadgeShape.Rectangle
                                    }
                                    withHint={isMydays}
                                    primaryColor={discountBadge?.primaryColor ?? ''}
                                    secondaryColor={discountBadge?.secondaryColor}
                                    textColor={discountBadge?.textColor ?? ''}
                                    isBig
                                    className={classNames(
                                        'position-absolute',
                                        styles.discountBadgeWrapper,
                                        {
                                            [styles.borderRadiusTopLeft]: !isMydays,
                                            [styles.roundedSM0]: !isMydays,
                                        },
                                    )}
                                />
                            )}
                            {mediaItem.kind === MediaKind.IMAGE ? (
                                <Image
                                    onClick={handleImageClick}
                                    className={classNames(
                                        'w-100 h-100 d-block',
                                        styles.carouselImage,
                                        withImagePreview && 'rounded-2',
                                    )}
                                    src={mediaItem.src}
                                    alt={`${mediaItem.alt || title} - ${index + 1}`}
                                    srcSet={mediaItem.srcset}
                                    sizes={mediaItem.sizes}
                                    lazyLoad={false}
                                    fetchPriority={index === 0 ? 'high' : 'low'}
                                    width={currentDimensions.width}
                                    height={currentDimensions.height}
                                />
                            ) : (
                                <Video
                                    className={classNames(
                                        'w-100 h-100 d-block',
                                        styles.carouselImage,
                                        withImagePreview && 'rounded-2',
                                    )}
                                    poster={mediaItem.poster}
                                    ariaLabel={`${title} - ${index + 1}`}
                                    width={currentDimensions.width}
                                    index={index}
                                    thumbnailAlt={mediaItem.alt ?? title}
                                    height={currentDimensions.height}
                                    sources={mediaItem.sources}
                                    withImagePreview
                                    controls
                                    onFullscreenChange={handleVideoFullscreenChange}
                                    durationInSeconds={mediaItem.durationInSeconds}
                                />
                            )}
                        </div>
                    </ResponsiveFrame>
                ),
            }))}
            a11yPreviousSlide={intl.formatMessage(messages.previousSlide)}
            a11yNextSlide={intl.formatMessage(messages.nextSlide)}
        />
    );
};
